import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Shield, Loader2, Users, AlertTriangle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const SecurityAnalysis = ({ project }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [project.id]);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API}/projects/${project.id}/discovery/security`);
      setData(response.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !data) return <Loader2 className="w-12 h-12 animate-spin mx-auto mt-20" />;

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
        <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          <Shield className="w-6 h-6 mr-3 text-red-600" />
          Security Analysis
        </h3>
        
        <div className="grid md:grid-cols-4 gap-4 mb-8">
          <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
            <p className="text-sm text-blue-600 font-medium mb-1">Total Roles</p>
            <p className="text-3xl font-bold text-blue-900">{data.roles.total_roles}</p>
          </div>
          <div className="bg-purple-50 border-2 border-purple-200 rounded-xl p-4">
            <p className="text-sm text-purple-600 font-medium mb-1">Total Users</p>
            <p className="text-3xl font-bold text-purple-900">{data.users.total_users}</p>
          </div>
          <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4">
            <p className="text-sm text-green-600 font-medium mb-1">Active Users</p>
            <p className="text-3xl font-bold text-green-900">{data.users.active_users}</p>
          </div>
          <div className="bg-orange-50 border-2 border-orange-200 rounded-xl p-4">
            <p className="text-sm text-orange-600 font-medium mb-1">Assignments</p>
            <p className="text-3xl font-bold text-orange-900">{data.assignments.total_assignments}</p>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200 rounded-xl p-5">
            <h4 className="text-lg font-bold text-gray-900 mb-4">Role Breakdown</h4>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-700">Composite Roles:</span>
                <span className="font-bold text-gray-900">{data.roles.composite_roles}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700">Single Roles:</span>
                <span className="font-bold text-gray-900">{data.roles.single_roles}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700">Custom Roles:</span>
                <span className="font-bold text-gray-900">{data.roles.custom_roles}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700">Standard Roles:</span>
                <span className="font-bold text-gray-900">{data.roles.standard_roles}</span>
              </div>
            </div>
          </div>

          <div className="bg-red-50 border-2 border-red-200 rounded-xl p-5">
            <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
              <AlertTriangle className="w-5 h-5 mr-2 text-red-600" />
              Security Issues
            </h4>
            <div className="space-y-2">
              {data.issues.map((issue, idx) => (
                <div key={idx} className="flex items-start space-x-2 p-3 bg-white rounded-lg">
                  <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-800 font-medium">{issue}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};